import { useCallback, useEffect, useState } from "react"

import { Button, HStack } from "@chakra-ui/react"
import { useLocation, useNavigate, useSearchParams } from "react-router-dom"

import { useAppDispatch, useAppSelector } from "app/hooks"
import AddTagModal from "components/add-tag-modal/AddTagModal"
import ViewTagMenu from "components/view-tag-menu/ViewTagMenu"
import useIsPermitted from "hooks/useIsPermittedHook/useIsPermitted"
import useToastHook from "hooks/useToastHook/useToastHook"
import { apiClient } from "services/client"
import { getCompany, selectCurrentPmc } from "store/auth/authSlice"
import { fetchFeeCategoryData } from "store/features/featuresSlice"

import { TabsTypeProps } from "./types"

const Tabs = (props: TabsTypeProps) => {
  const { tabsArr } = props
  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useAppDispatch()
  const currentPmc = useAppSelector(selectCurrentPmc)
  const hasAccessTo = useIsPermitted()
  const [searchParams] = useSearchParams()
  const displayToast = useToastHook()

  const [tagOrder, setTagOrder] = useState<string[]>([])
  const [isLoadingTags, setIsLoadingTags] = useState(false)

  const getAdminCompanyTag = useCallback(async () => {
    if (currentPmc?.pmc_id) {
      await dispatch(
        getCompany({
          params: {
            pmcId: currentPmc?.pmc_id,
          },
        }),
      ).unwrap()
    }
  }, [currentPmc?.pmc_id, dispatch])

  const getFeeCategory = useCallback(async () => {
    if (currentPmc?.pmc_id) {
      await dispatch(
        fetchFeeCategoryData({
          params: {
            pmcId: currentPmc?.pmc_id,
            categoryName: searchParams.get("categoryName") || "ALL",
            keyword: searchParams.get("keyword") || "",
            limit: Number(searchParams?.get("noOfRecords")) || 5,
            skip:
              Number(searchParams?.get("noOfRecords")) *
              Number(searchParams?.get("pageNo")),
            sortBy: searchParams?.get("sortBy") || "",
            sortDirection: "ASC",
          },
        }),
      ).unwrap()
    }
  }, [currentPmc?.pmc_id, searchParams, dispatch])

  useEffect(() => {
    // eslint-disable-next-line no-void
    void getAdminCompanyTag()
  }, [currentPmc?.pmc_id, getAdminCompanyTag])

  // Fetch the available tags from the API
  useEffect(() => {
    // If the user is not logged in, return early
    if (!currentPmc?.pmc_id) return
    const fetchCompanyTags = async () => {
      try {
        setIsLoadingTags(true)

        const response = await apiClient.getCompanyTags()

        if (response?.data?.apiStatus === "SUCCESS") {
          setTagOrder(response?.data?.data || [])
        } else {
          displayToast({
            message: "Failed to load company tags",
            variant: "error",
          })
        }
      } catch (err) {
        displayToast({
          message: "Failed to load company tags",
          variant: "error",
        })
      } finally {
        setIsLoadingTags(false)
      }
    }

    // eslint-disable-next-line no-void
    void fetchCompanyTags()

    // NOTE: do not add displayToast in dependency array as it causes infinite loop of API calls
  }, [currentPmc?.pmc_id])

  return (
    <HStack
      spacing={{ base: 0, md: 4 }}
      display={{ base: "block", md: "flex" }}
      justifyContent='space-between'
    >
      <HStack>
        {tabsArr &&
          tabsArr.map((tab) => (
            <Button
              key={tab?.name}
              size='sm'
              variant={
                location.pathname.startsWith(tab?.basePath)
                  ? "primary_nav_active"
                  : "primary_nav"
              }
              w={{ base: "full", md: "auto" }}
              h={{ base: "48px", md: "auto" }}
              textTransform={{ base: "uppercase", md: "initial" }}
              fontSize='sm'
              onClick={() => navigate(tab?.path, { replace: true })}
            >
              {tab?.name}
            </Button>
          ))}
      </HStack>
      <HStack
        paddingRight={{ xsm: "15px", lg2: "35px" }}
        justifyContent={{ base: "end", md: "space-between" }}
        paddingY={{ base: "10px", md: "0px" }}
      >
        {hasAccessTo?.isAdmin && (
          <>
            <ViewTagMenu tagOrder={tagOrder} />
            <AddTagModal
              getCompany={() => {
                // eslint-disable-next-line no-void
                void getAdminCompanyTag()
                // eslint-disable-next-line no-void
                void getFeeCategory()
              }}
              tagOrder={tagOrder}
              isLoadingTags={isLoadingTags}
            />
          </>
        )}
      </HStack>
    </HStack>
  )
}
export default Tabs
